/**
 * businessInsightsProductsData.ts — Tarjetas demo de productos e inventario.
 *
 * QUÉ HACE: declara las tarjetas del explorador de Análisis del negocio del
 *   grupo productos e inventario (top productos, rotación, valor de inventario,
 *   estancados, ventas cruzadas, clasificación ABC, cobertura de stock, capital
 *   por antigüedad, próximos a vencer, verificación y movimientos de stock,
 *   productos agotados y lotes vencidos) con datos DEMO deterministas para la
 *   pestaña de pruebas del entorno local.
 *
 * QUÉ INVOCA: helpers de `businessInsights.shared.ts`.
 * QUÉ EXPORTA: BUSINESS_INSIGHTS_PRODUCTS (readonly BusinessInsightDataset[]).
 * QUÉ NO DEBE HACER: no lee storage, no hace fetch, no conoce React.
 */
import { Boxes, Link2, Package, PackageX, RefreshCw } from "lucide-react";
import type { BusinessInsightDataset } from "../types/businessInsights.types";
import {
  INSIGHT_COLORS,
  INSIGHT_VIEW_PRESETS,
  insightRow,
} from "./businessInsights.shared";

export const BUSINESS_INSIGHTS_PRODUCTS: readonly BusinessInsightDataset[] = [
  {
    id: "top-productos",
    title: "Top 10 productos",
    subtitle: "Ingresos por producto — último mes",
    icon: Package,
    group: "productos",
    valueKind: "currency",
    nameLabel: "Producto",
    series: [
      { key: "ingresos", label: "Ingresos", color: INSIGHT_COLORS.caja },
    ],
    rows: [
      insightRow("Nevera No Frost 400L", { ingresos: 30690000 }),
      insightRow('TV 55" Smart', { ingresos: 22100000 }),
      insightRow("Lavadora 18kg", { ingresos: 18400000 }),
      insightRow("Parlante Bluetooth", { ingresos: 12600000 }),
      insightRow("Audífonos BT", { ingresos: 9800000 }),
      insightRow("Microondas 25L", { ingresos: 7400000 }),
      insightRow("Freidora de aire", { ingresos: 6100000 }),
      insightRow("Licuadora 2L", { ingresos: 4200000 }),
      insightRow("Plancha cerámica", { ingresos: 3500000 }),
      insightRow("Cafetera 12 tazas", { ingresos: 2900000 }),
    ],
    views: INSIGHT_VIEW_PRESETS.rankingHorizontal,
    badge: { mode: "max", label: "Estrella", tone: "green" },
  },
  {
    id: "rotacion-inventario",
    title: "Rotación de inventario",
    subtitle: "Veces que se vende el stock — último mes",
    icon: RefreshCw,
    group: "productos",
    valueKind: "decimal",
    valueSuffix: "x/mes",
    nameLabel: "Producto",
    series: [
      { key: "rotacion", label: "Rotación", color: INSIGHT_COLORS.info },
    ],
    rows: [
      insightRow("Nevera No Frost 400L", { rotacion: 4.2 }),
      insightRow('TV 55" Smart', { rotacion: 3.1 }),
      insightRow("Parlante Bluetooth", { rotacion: 2.4 }),
      insightRow("Licuadora 2L", { rotacion: 2.1 }),
      insightRow("Microondas 25L", { rotacion: 1.8 }),
      insightRow("Lavadora 18kg", { rotacion: 1.2 }),
      insightRow("Freidora de aire", { rotacion: 0.9 }),
      insightRow("Cafetera 12 tazas", { rotacion: 0.3 }),
    ],
    views: ["semaforo", ...INSIGHT_VIEW_PRESETS.rankingHorizontal],
    defaultView: "semaforo",
    status: {
      valueKey: "rotacion",
      okAt: 3,
      warnAt: 1.5,
      labels: { ok: "Saludable", warn: "Lento", bad: "Crítico" },
    },
    badge: { mode: "max", label: "Mayor rotación", tone: "blue" },
  },
  {
    id: "valor-inventario",
    title: "Valor de inventario",
    subtitle: "Capital en bodega por categoría — $200M",
    icon: Boxes,
    group: "productos",
    valueKind: "currency",
    nameLabel: "Categoría",
    series: [{ key: "valor", label: "Valor", color: INSIGHT_COLORS.violeta }],
    rows: [
      insightRow("Electrodomésticos", { valor: 95000000 }),
      insightRow("Tecnología", { valor: 52000000 }),
      insightRow("Hogar", { valor: 33000000 }),
      insightRow("Accesorios", { valor: 12000000 }),
      insightRow("Otros", { valor: 8000000 }),
    ],
    views: INSIGHT_VIEW_PRESETS.rankingHorizontal,
    badge: { mode: "total", label: "Total", tone: "blue" },
  },
  {
    id: "productos-estancados",
    title: "Productos estancados",
    subtitle: "Sin ventas en 60+ días — capital parado",
    icon: PackageX,
    group: "productos",
    valueKind: "currency",
    nameLabel: "Producto",
    series: [
      { key: "valor", label: "Capital parado", color: INSIGHT_COLORS.naranja },
    ],
    rows: [
      insightRow("Cafetera 12 tazas", { valor: 4200000 }, "85 días sin venta"),
      insightRow("Ventilador torre", { valor: 2800000 }, "72 días sin venta"),
      insightRow("Plancha cerámica", { valor: 1100000 }, "61 días sin venta"),
      insightRow("Set de sartenes", { valor: 600000 }, "78 días sin venta"),
      insightRow("Exprimidor manual", { valor: 400000 }, "66 días sin venta"),
      insightRow("Termo 1L", { valor: 200000 }, "90 días sin venta"),
    ],
    views: [...INSIGHT_VIEW_PRESETS.tablaFirst, "radar"],
    defaultView: "tabla",
    badge: { mode: "total", label: "Capital parado", tone: "amber" },
  },
  {
    id: "ventas-cruzadas",
    title: "Ventas cruzadas",
    subtitle: "Afinidad entre productos — se compran juntos",
    icon: Link2,
    group: "productos",
    valueKind: "decimal",
    valueSuffix: "%",
    nameLabel: "Combinación",
    series: [
      { key: "afinidad", label: "Afinidad", color: INSIGHT_COLORS.violeta },
    ],
    rows: [
      insightRow("Nevera + Garantía extendida", { afinidad: 68 }),
      insightRow("TV + Soporte pared", { afinidad: 54 }),
      insightRow("Lavadora + Jabón", { afinidad: 41 }),
      insightRow("Celular + Vidrio templado", { afinidad: 39 }),
      insightRow("Freidora + Aceite", { afinidad: 32 }),
      insightRow("Cafetera + Café molido", { afinidad: 27 }),
    ],
    views: INSIGHT_VIEW_PRESETS.distribucionHorizontal,
    badge: { mode: "max", label: "Mayor afinidad", tone: "blue" },
  },
];
